import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Clock, Zap, Cpu, Loader2, Shield, Plus, Trash2, Pencil,
  Bot, Key, Lock, Eye, EyeOff,
  X, Check, Mail, Settings as SettingsIcon, Link2, RefreshCw,
  Layout, ShieldAlert, Globe, FileSearch2, Database, Sliders,
  ChevronsLeft, Code
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import { supabase } from '../../lib/supabase';
import api from '../../api';
import { cn } from '../../lib/utils';
import GoogleAccountsPanel from './GoogleAccountsPanel';

const ACCESS_LEVELS = [
  { value: 1, label: 'Level 1 — Full Access', role: 'super_admin' },
  { value: 2, label: 'Level 2 — Branch Admin', role: 'branch_admin' },
  { value: 3, label: 'Level 3 — Batch Admin', role: 'batch_admin' },
  { value: 4, label: 'Level 4 — Course Admin', role: 'course_admin' },
  { value: 5, label: 'Level 5 — Content Manager', role: 'content_manager' },
  { value: 6, label: 'Level 6 — Inquiry Admin', role: 'inquiry_admin' },
  { value: 7, label: 'Level 7 — Attendance Manager', role: 'attendance_manager' },
  { value: 8, label: 'Level 8 — Faculty', role: 'faculty' },
  { value: 9, label: 'Level 9 — View Only', role: 'viewer' },
  { value: 10, label: 'Level 10 — Doubt Solver', role: 'doubt_expert' },
];

const LEVEL_COLORS = {
  1: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  2: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  3: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  4: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  5: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  6: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  7: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  8: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  9: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
  10: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

function getProfile() {
  try { return JSON.parse(localStorage.getItem('admin_profile') || '{}'); } catch { return {}; }
}

function SettingsSkeleton() {
  return (
    <div className="flex flex-col md:flex-row h-full bg-white dark:bg-zinc-900 animate-in fade-in duration-500">
      {/* Sidebar Skeleton */}
      <aside className="w-full md:w-64 border-r border-zinc-200 dark:border-zinc-800 flex flex-col shrink-0 bg-zinc-50/50 dark:bg-zinc-950/20">
        <div className="p-6 border-b border-zinc-200/50 dark:border-zinc-800/50 mb-4">
          <div className="flex items-center gap-2">
            <Skeleton className="w-5 h-5 rounded-md" />
            <Skeleton className="h-6 w-24" />
          </div>
        </div>
        <div className="flex-1 px-3 space-y-8">
          {[1, 2, 3].map(section => (
            <div key={section} className="space-y-2">
              <div className="px-3">
                <Skeleton className="h-2.5 w-20" />
              </div>
              <div className="space-y-1">
                {[1, 2].map(item => (
                  <div key={item} className="px-3">
                    <Skeleton className="h-10 w-full rounded-xl" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
          <Skeleton className="h-10 w-full rounded-lg shadow-sm" />
        </div>
      </aside>

      {/* Main Content Skeleton */}
      <main className="flex-1 overflow-hidden flex flex-col relative">
        <div className="absolute inset-0 bg-grid-zinc-200/[0.05] -z-10" />
        <div className="max-w-3xl mx-auto px-6 py-8 md:px-16 lg:px-24 w-full space-y-8">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-80" />
          </div>

          <div className="grid gap-6">
            {[1, 2].map(i => (
              <div key={i} className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 space-y-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map(j => (
                    <div key={j} className="space-y-2">
                      <Skeleton className="h-3 w-28" />
                      <Skeleton className="h-10 w-full rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

function AdminManagementSkeleton() {
  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32 rounded-lg" />
      </div>
      <div className="grid gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 flex items-center justify-between gap-4 shadow-sm">
            <div className="flex items-center gap-4">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-16 rounded-full" />
                </div>
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="w-8 h-8 rounded-lg" />
              <Skeleton className="w-8 h-8 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [mobileShowContent, setMobileShowContent] = useState(false);
  const [resettingDrafts, setResettingDrafts] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [deletePastEvents, setDeletePastEvents] = useState(false);
  const [deleteLockedEvents, setDeleteLockedEvents] = useState(false);

  // Join code states
  const [joinCode, setJoinCode] = useState('');
  const [loadingJoinCode, setLoadingJoinCode] = useState(false);
  const [regeneratingJoinCode, setRegeneratingJoinCode] = useState(false);

  // Change password states
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [showPws, setShowPws] = useState({ new: false, confirm: false });

  const fetchJoinCode = async () => {
    setLoadingJoinCode(true);
    try {
      const res = await api.get('/api/teams/join-code');
      setJoinCode(res.data.join_code);
    } catch (err) {
      console.error('Failed to fetch join code:', err);
    } finally {
      setLoadingJoinCode(false);
    }
  };

  const regenerateJoinCode = async () => {
    if (!window.confirm("Are you sure you want to recreate the join code? The old code will immediately become invalid!")) {
      return;
    }
    setRegeneratingJoinCode(true);
    try {
      const res = await api.post('/api/teams/regenerate-code');
      setJoinCode(res.data.join_code);
      toast.success("Join code successfully recreated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to recreate join code");
    } finally {
      setRegeneratingJoinCode(false);
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPw || !confirmPw) {
      toast.error("New password and confirmation are required");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPw });
      if (error) throw error;
      toast.success("Password updated successfully!");
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      toast.error(err.message || "Failed to update password");
    } finally {
      setUpdatingPassword(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'joincode') {
      fetchJoinCode();
    }
  }, [activeTab]);

  const handleResetDrafts = () => {
    setDeletePastEvents(false);
    setDeleteLockedEvents(false);
    setShowResetModal(true);
  };

  const handleConfirmReset = async () => {
    setShowResetModal(false);
    setResettingDrafts(true);
    try {
      const res = await api.post('/api/scheduling/reset-drafts', {
        delete_past_events: deletePastEvents,
        delete_locked_events: deleteLockedEvents,
      });
      toast.success(res.data?.message || 'Successfully reset schedule!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset schedule');
    } finally {
      setResettingDrafts(false);
    }
  };

  const profile = useMemo(() => getProfile(), []);
  const isLevel1 = profile.access_level === 1;

  const fetchSettings = async () => {
    const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
    try {
      // Fetch Team (for settings and API keys)
      if (profile.team_id) {
        const response = await api.get('/api/team');
        const tData = response.data;
        if (tData) {
          // Initialize api_keys if null
          if (!tData.api_keys) tData.api_keys = {};
          
          // Migration/Compatibility: If individual columns have values but api_keys doesn't, sync them locally
          const keys = ['groq_api_key', 'openai_api_key', 'anthropic_api_key', 'google_api_key', 'xai_api_key'];
          keys.forEach(k => {
            if (tData[k] && !tData.api_keys[k]) {
              tData.api_keys[k] = tData[k];
            }
          });
          
          setTeam(tData);
          
          if (tData.settings) {
            setSettings(tData.settings);
          } else {
            setSettings({
              team_id: profile.team_id,
              working_hours_start: '09:00',
              working_hours_end: '18:00',
              max_hours_per_day: 8,
              slot_duration_minutes: 30,
              auto_sync: true,
              sync_interval_minutes: 60,
              schedule_lock_hours: 24,
              rolling_window_weeks: 4,
              draft_resolution_weeks: 2,
              autonomous_scheduling_enabled: false
            });
          }
        }
      }

    } catch (err) {
      console.error(err);
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  useEffect(() => { fetchSettings(); }, []);

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const saveSettings = async () => {
    try {
      // Save Team (API Keys and settings)
      if (team) {
        const { id: tId, created_at, updated_at: tUpdated, ...tData } = team;
        
        // Ensure api_keys and settings are prioritized
        const payload = {
          name: team.name,
          api_keys: team.api_keys || {},
          settings: settings || {}
        };

        const response = await api.put('/api/team', payload);
        if (response.data) {
          setTeam(response.data);
          setSettings(response.data.settings || {});
        }
      }

      toast.success('Settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || err.message || 'Failed to save settings'); }
  };

  const SECTIONS = [
    {
      title: 'System configuration',
      items: [
        { id: 'general', label: 'General Settings', icon: Globe },
        { id: 'algorithm', label: 'Algorithm Defaults', icon: Cpu },
        { id: 'autonomous', label: 'Autonomous Scheduling', icon: Zap },
      ]
    },
    {
      title: 'Integrations',
      items: [
        { id: 'google', label: 'Google Sync', icon: Link2, disabled: true },
      ]
    },
    {
      title: 'AI & Cloud',
      items: [
        { id: 'documents', label: 'Doc Processing', icon: FileSearch2 },
      ]
    },
    {
      title: 'Administration',
      items: [
        { id: 'admins', label: 'Manage Admins', icon: Shield, minLevel: 1 },
        { id: 'joincode', label: 'Team Join Code', icon: Code, minLevel: 1 },
      ],
    },
    {
      title: 'Security',
      items: [
        { id: 'security', label: 'Change Password', icon: Key },
      ]
    }
  ];

  const filteredSections = SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => !item.minLevel || profile.access_level <= item.minLevel)
  })).filter(section => section.items.length > 0);

  if (loading || !settings) return <SettingsSkeleton />;

  const handleTabChange = (id) => {
    setActiveTab(id);
    setMobileShowContent(true);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'autonomous':
        return (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
              <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Autonomous Scheduling</h2>
              <p className="text-sm text-zinc-500">Configure rolling horizons, imminent lock hours, and run schedule initialization/updates</p>
            </div>

            <section className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 space-y-6">
              <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800 pb-4">
                <div className="space-y-0.5">
                  <Label className="text-sm font-semibold">Enable Autonomous Scheduling</Label>
                  <p className="text-xs text-zinc-500">Master switch to trigger automatic conflict-resolution & rolling updates</p>
                </div>
                <Switch
                  checked={settings.autonomous_scheduling_enabled ?? false}
                  onCheckedChange={checked => updateSetting('autonomous_scheduling_enabled', checked)}
                />
              </div>

              <div className="space-y-6 pt-2">
                {/* Lock hours */}
                <div className="space-y-3">
                  <div className="flex justify-between items-end">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-semibold">Schedule Lock-in Period</Label>
                      <p className="text-xs text-zinc-500">Number of hours before a lecture where it becomes locked & immune to repair engine (6 – 168 hours)</p>
                    </div>
                    <span className="text-sm font-bold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded leading-none">
                      {settings.schedule_lock_hours ?? 24} hrs
                    </span>
                  </div>
                  <input
                    type="range" min="6" max="168"
                    value={settings.schedule_lock_hours ?? 24}
                    onChange={e => updateSetting('schedule_lock_hours', parseInt(e.target.value) || 24)}
                    className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="relative w-full h-6 mt-2">
                    {/* Ticks container */}
                    <div className="absolute top-0 left-0 w-full h-1 flex pointer-events-none">
                      {[6, 24, 72, 168].map((val) => {
                        const percent = ((val - 6) / (168 - 6)) * 100;
                        return (
                          <div
                            key={val}
                            className="absolute w-0.5 h-1.5 bg-zinc-200 dark:bg-zinc-800 -translate-x-1/2"
                            style={{ left: `${percent}%` }}
                          />
                        );
                      })}
                    </div>
                    {/* Labels container */}
                    <div className="absolute top-3 left-0 w-full">
                      {[
                        { value: 6, label: '6 Hours' },
                        { value: 24, label: '24 Hours (1 Day)' },
                        { value: 72, label: '72 Hours (3 Days)' },
                        { value: 168, label: '168 Hours (1 Week)' }
                      ].map((tick, index, arr) => {
                        const percent = ((tick.value - 6) / (168 - 6)) * 100;
                        let alignmentClass = "-translate-x-1/2";
                        if (index === 0) alignmentClass = "translate-x-0";
                        if (index === arr.length - 1) alignmentClass = "-translate-x-full";
                        return (
                          <span
                            key={tick.value}
                            className={cn(
                              "absolute text-[10px] text-zinc-400 dark:text-zinc-500 font-medium whitespace-nowrap transition-all duration-200",
                              alignmentClass
                            )}
                            style={{ left: `${percent}%` }}
                          >
                            {tick.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Rolling Window */}
                <div className="space-y-3">
                  <div className="flex justify-between items-end">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-semibold">Rolling Window (Solidified Weeks)</Label>
                      <p className="text-xs text-zinc-500">Weeks ahead generated as high-quality 'solid' lecture schedules (1 – 16 weeks)</p>
                    </div>
                    <span className="text-sm font-bold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded leading-none">
                      {settings.rolling_window_weeks ?? 4} weeks
                    </span>
                  </div>
                  <input
                    type="range" min="1" max="16"
                    value={settings.rolling_window_weeks ?? 4}
                    onChange={e => updateSetting('rolling_window_weeks', parseInt(e.target.value) || 4)}
                    className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="relative w-full h-6 mt-2">
                    {/* Ticks container */}
                    <div className="absolute top-0 left-0 w-full h-1 flex pointer-events-none">
                      {[1, 4, 8, 16].map((val) => {
                        const percent = ((val - 1) / (16 - 1)) * 100;
                        return (
                          <div
                            key={val}
                            className="absolute w-0.5 h-1.5 bg-zinc-200 dark:bg-zinc-800 -translate-x-1/2"
                            style={{ left: `${percent}%` }}
                          />
                        );
                      })}
                    </div>
                    {/* Labels container */}
                    <div className="absolute top-3 left-0 w-full">
                      {[
                        { value: 1, label: '1 Week' },
                        { value: 4, label: '4 Weeks (1 Month)' },
                        { value: 8, label: '8 Weeks (2 Months)' },
                        { value: 16, label: '16 Weeks (Full Term)' }
                      ].map((tick, index, arr) => {
                        const percent = ((tick.value - 1) / (16 - 1)) * 100;
                        let alignmentClass = "-translate-x-1/2";
                        if (index === 0) alignmentClass = "translate-x-0";
                        if (index === arr.length - 1) alignmentClass = "-translate-x-full";
                        return (
                          <span
                            key={tick.value}
                            className={cn(
                              "absolute text-[10px] text-zinc-400 dark:text-zinc-500 font-medium whitespace-nowrap transition-all duration-200",
                              alignmentClass
                            )}
                            style={{ left: `${percent}%` }}
                          >
                            {tick.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Draft Resolution */}
                <div className="space-y-3">
                  <div className="flex justify-between items-end">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-semibold">Draft Resolution Window</Label>
                      <p className="text-xs text-zinc-500">Low-resolution projected weeks drafted beyond the solid window (1 – 8 weeks)</p>
                    </div>
                    <span className="text-sm font-bold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded leading-none">
                      {settings.draft_resolution_weeks ?? 2} weeks
                    </span>
                  </div>
                  <input
                    type="range" min="1" max="8"
                    value={settings.draft_resolution_weeks ?? 2}
                    onChange={e => updateSetting('draft_resolution_weeks', parseInt(e.target.value) || 2)}
                    className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="relative w-full h-6 mt-2">
                    {/* Ticks container */}
                    <div className="absolute top-0 left-0 w-full h-1 flex pointer-events-none">
                      {[1, 2, 4, 8].map((val) => {
                        const percent = ((val - 1) / (8 - 1)) * 100;
                        return (
                          <div
                            key={val}
                            className="absolute w-0.5 h-1.5 bg-zinc-200 dark:bg-zinc-800 -translate-x-1/2"
                            style={{ left: `${percent}%` }}
                          />
                        );
                      })}
                    </div>
                    {/* Labels container */}
                    <div className="absolute top-3 left-0 w-full">
                      {[
                        { value: 1, label: '1 Week' },
                        { value: 2, label: '2 Weeks' },
                        { value: 4, label: '4 Weeks' },
                        { value: 8, label: '8 Weeks' }
                      ].map((tick, index, arr) => {
                        const percent = ((tick.value - 1) / (8 - 1)) * 100;
                        let alignmentClass = "-translate-x-1/2";
                        if (index === 0) alignmentClass = "translate-x-0";
                        if (index === arr.length - 1) alignmentClass = "-translate-x-full";
                        return (
                          <span
                            key={tick.value}
                            className={cn(
                              "absolute text-[10px] text-zinc-400 dark:text-zinc-500 font-medium whitespace-nowrap transition-all duration-200",
                              alignmentClass
                            )}
                            style={{ left: `${percent}%` }}
                          >
                            {tick.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Schedule Maintenance / Reset section */}
            <section className="bg-red-50/50 dark:bg-red-950/10 rounded-xl border border-red-200/60 dark:border-red-900/40 p-6 space-y-4">
              <div>
                <h3 className="text-sm font-bold text-red-800 dark:text-red-400 uppercase tracking-wider">Reset Schedule</h3>
                <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                  Wipes generated scheduling data with granular controls over past, rolling, and locked lectures.
                  Resets the synchronization state and last run timestamp so the scheduling engine starts fresh.
                </p>
              </div>
              <div>
                <Button
                  variant="destructive"
                  onClick={handleResetDrafts}
                  disabled={resettingDrafts}
                  className="h-9 px-4 rounded-xl text-[9px] font-black uppercase tracking-wider flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/10"
                >
                  {resettingDrafts ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                  Reset Schedule
                </Button>
              </div>
            </section>
          </div>
      );
      case 'general':
        return (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
              <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">General Settings</h2>
              <p className="text-sm text-zinc-500">Core operational parameters for the scheduling system</p>
            </div>

            <div className="grid gap-6">
              <section className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Working Hours</h3>
                    <p className="text-xs text-zinc-500">Define your standard daily operating window</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Start Time</Label>
                    <Input type="time" value={settings.working_hours_start} onChange={e => updateSetting('working_hours_start', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">End Time</Label>
                    <Input type="time" value={settings.working_hours_end} onChange={e => updateSetting('working_hours_end', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Maximum Daily Load (Hours)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={settings.max_hours_per_day}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '');
                        updateSetting('max_hours_per_day', parseInt(val) || 0);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Slot Granularity (Minutes)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={settings.slot_duration_minutes}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '');
                        updateSetting('slot_duration_minutes', parseInt(val) || 0);
                      }}
                    />
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Automation Settings</h3>
                    <p className="text-xs text-zinc-500">Control how the system handles background tasks</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Automatic Synchronization</Label>
                    <p className="text-xs text-zinc-500">Automatically push changes to external calendars</p>
                  </div>
                  <Switch checked={false} disabled />
                </div>
              </section>
            </div>
          </div>
        );
      case 'algorithm':
        return (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
              <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Algorithm Defaults</h2>
              <p className="text-sm text-zinc-500">Influence how the scheduler prioritizes different variables (0-10)</p>
            </div>

            <section className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
              <div className="p-6 space-y-8">
                {[
                  { key: 'scheduler_morning_weight', label: 'Morning Bias', desc: 'Prioritizes slots earlier in the daily window.' },
                  { key: 'scheduler_preference_weight', label: 'Faculty Preferences', desc: 'Weight given to specific availability requests from faculty.' },
                  { key: 'scheduler_compactness_weight', label: 'Schedule Compactness', desc: 'Increases the likelihood of consecutive classes for students.' },
                  { key: 'scheduler_activation_weight', label: 'Day Consolidation', desc: 'Attempts to minimize the number of unique days a student must attend.' },
                ].map((item) => (
                  <div key={item.key} className="space-y-3">
                    <div className="flex justify-between items-end">
                      <div className="space-y-0.5">
                        <Label className="text-sm font-semibold">{item.label}</Label>
                        <p className="text-xs text-zinc-500">{item.desc}</p>
                      </div>
                      <span className="text-sm font-bold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded leading-none">{settings[item.key] ?? 5}</span>
                    </div>
                    <input
                      type="range" min="0" max="10"
                      value={settings[item.key] ?? 5}
                      onChange={e => updateSetting(item.key, parseInt(e.target.value) || 0)}
                      className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                  </div>
                ))}
              </div>
            </section>
          </div>
        );
      case 'google':
        return <div className="animate-in fade-in slide-in-from-bottom-2 duration-300"><GoogleAccountsPanel isSettings /></div>;
      case 'admins':
        return <div className="animate-in fade-in slide-in-from-bottom-2 duration-300"><AdminManagementSection /></div>;
      case 'documents':
        return (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
              <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Document Processing</h2>
              <p className="text-sm text-zinc-500">Configure the AI ingestion pipeline, chunking behavior, and vector store settings</p>
            </div>

            {/* Chunking */}
            <section className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                  <Sliders className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Chunking Configuration</h3>
                  <p className="text-xs text-zinc-500">Controls how documents are split into searchable sections</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Default Chunk Size (tokens)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    defaultValue={800}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Chunk Overlap (tokens)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    defaultValue={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Default Content Type</Label>
                  <select className="flex h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:border-zinc-800 dark:bg-zinc-950">
                    <option>Theory</option>
                    <option>Definition</option>
                    <option>Example</option>
                    <option>Solved Problem</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Auto-approve Ingested Chunks</Label>
                  <div className="flex items-center gap-3 h-10">
                    <Switch defaultChecked={false} />
                    <span className="text-sm text-zinc-500">Manually review in Content Explorer</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Embedding provider */}
            <section className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                  <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Embedding Provider</h3>
                  <p className="text-xs text-zinc-500">Model used to convert chunks into vector representations</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Provider</Label>
                  <select className="flex h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:border-zinc-800 dark:bg-zinc-950">
                    <option>OpenAI (text-embedding-3-small)</option>
                    <option>OpenAI (text-embedding-3-large)</option>
                    <option>Google (embedding-001)</option>
                    <option>Local / Ollama (nomic-embed-text)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Embedding Dimensions</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    defaultValue={1536}
                    disabled
                  />
                </div>
              </div>
            </section>

            {/* Vector store */}
            <section className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                  <Database className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Vector Store</h3>
                  <p className="text-xs text-zinc-500">Storage backend for chunk embeddings and semantic search</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Backend</Label>
                  <select className="flex h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:border-zinc-800 dark:bg-zinc-950">
                    <option>Supabase pgvector (built-in)</option>
                    <option>Pinecone</option>
                    <option>Qdrant</option>
                    <option>Weaviate</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Vector Store API Key / Connection String</Label>
                  <Input type="password" placeholder="sk-pinecone-…" />
                </div>
              </div>
            </section>

            {/* System prompts */}
            <section className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">System Prompts</h3>
                  <p className="text-xs text-zinc-500">Customize how the chatbot frames context and responses</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Retrieval System Prompt</Label>
                <textarea
                  className="w-full min-h-[100px] rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none font-mono"
                  defaultValue={"You are a helpful academic assistant. Use the provided context from the knowledge base to answer questions accurately. Always cite the source chapter and subject when referencing content."}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Top-K Results</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  defaultValue={5}
                />
              </div>
            </section>
          </div>
        );
      case 'joincode':
        return (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
              <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Team Join Code</h2>
              <p className="text-sm text-zinc-500 font-medium">View, copy, or regenerate the secure access join code for your team</p>
            </div>

            <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-6 md:p-8 space-y-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Code className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-zinc-800 dark:text-zinc-200">Active Invitation Code</h3>
                  <p className="text-xs text-zinc-500 font-medium mt-0.5">Share this code with members so they can self-onboard and set up their password</p>
                </div>
              </div>

              <div className="flex flex-col items-center justify-center py-6 bg-zinc-50 dark:bg-zinc-950/40 rounded-2xl border border-zinc-100 dark:border-zinc-900">
                {loadingJoinCode ? (
                  <div className="flex items-center gap-2 py-4">
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    <span className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">Fetching secure code...</span>
                  </div>
                ) : (
                  <div className="space-y-4 text-center">
                    <div 
                      onClick={() => {
                        if (joinCode) {
                          navigator.clipboard.writeText(joinCode);
                          toast.success("Join code copied to clipboard!");
                        }
                      }}
                      className="cursor-pointer group relative font-mono text-3xl font-black tracking-widest text-blue-600 dark:text-blue-400 bg-white dark:bg-zinc-900 px-8 py-4 rounded-xl border border-zinc-200 dark:border-zinc-800 shadow-sm inline-block hover:border-blue-500 transition-all hover:scale-105 active:scale-95"
                    >
                      {joinCode || "CS-XXXXXX"}
                      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-tighter bg-zinc-100 dark:bg-zinc-800 px-1 py-0.5 rounded">Copy</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">Click the code above to copy it</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 p-4 bg-amber-50/50 dark:bg-amber-950/10 border border-amber-200/60 dark:border-amber-900/40 rounded-xl text-xs text-amber-700 dark:text-amber-400 leading-relaxed font-medium">
                <ShieldAlert className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-500" />
                <span>
                  <strong>Security Warning:</strong> Anyone who possesses this code can register an account linked to your academic workspace. Only share this code with authorized members, and regenerate it immediately if you suspect unauthorized access.
                </span>
              </div>

              <div className="border-t border-zinc-100 dark:border-zinc-800 pt-5 flex justify-end">
                <Button 
                  onClick={regenerateJoinCode} 
                  disabled={regeneratingJoinCode || loadingJoinCode}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs h-10 px-4 rounded-xl shadow-lg shadow-blue-600/10 flex items-center gap-2"
                >
                  {regeneratingJoinCode ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  Regenerate Join Code
                </Button>
              </div>
            </section>
          </div>
        );
      case 'security':
        return (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
              <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 mb-1">Change Password</h2>
              <p className="text-sm text-zinc-500 font-medium">Update your account authentication credentials securely</p>
            </div>

            <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-6 md:p-8 space-y-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400">
                  <Key className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-zinc-800 dark:text-zinc-200">Update Credentials</h3>
                  <p className="text-xs text-zinc-500 font-medium mt-0.5">Choose a secure, complex password containing at least 6 characters</p>
                </div>
              </div>

              <form onSubmit={handleChangePasswordSubmit} className="space-y-5 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        type={showPws.new ? 'text' : 'password'}
                        value={newPw}
                        onChange={e => setNewPw(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10 pr-10 h-11 rounded-xl bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800"
                        required
                      />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 p-1" onClick={() => setShowPws(p => ({ ...p, new: !p.new }))}>
                        {showPws.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        type={showPws.confirm ? 'text' : 'password'}
                        value={confirmPw}
                        onChange={e => setConfirmPw(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10 pr-10 h-11 rounded-xl bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800"
                        required
                      />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 p-1" onClick={() => setShowPws(p => ({ ...p, confirm: !p.confirm }))}>
                        {showPws.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border-t border-zinc-100 dark:border-zinc-800 pt-5 flex justify-end">
                  <Button
                    type="submit"
                    disabled={updatingPassword}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs h-10 px-5 rounded-xl shadow-lg shadow-blue-600/10 flex items-center gap-2"
                  >
                    {updatingPassword ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    Update Password
                  </Button>
                </div>
              </form>
            </section>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-full bg-white dark:bg-zinc-900" data-testid="settings-panel">
      {/* Sidebar Layout */}
      <aside className={cn(
        "w-full md:w-64 border-r border-zinc-200 dark:border-zinc-800 flex flex-col shrink-0 bg-zinc-50/50 dark:bg-zinc-950/20 transition-all duration-300",
        mobileShowContent ? "hidden md:flex" : "flex"
      )}>
        <div className="p-6 md:p-6 pb-2 border-b border-zinc-200/50 dark:border-zinc-800/50 mb-4">
          <h1 className="text-lg md:text-xl font-bold text-zinc-900 dark:text-zinc-50 flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-blue-600" />
            Settings
          </h1>
          <p className="md:hidden text-xs text-zinc-500 mt-1">Configure your workspace preferences</p>
        </div>

        <ScrollArea className="flex-1 px-3">
          <nav className="space-y-8 pb-8 mt-2">
            {filteredSections.map((section, sIdx) => (
              <div key={sIdx} className="space-y-1">
                <h4 className="px-3 text-[0.65rem] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest mb-2">
                  {section.title}
                </h4>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => !item.disabled && handleTabChange(item.id)}
                      disabled={item.disabled}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-3 md:py-2 md:px-3 rounded-xl md:rounded-lg text-sm transition-all duration-200 group relative border border-transparent",
                        isActive
                          ? "bg-blue-600/5 dark:bg-blue-600/10 text-blue-600 dark:text-blue-400 font-semibold border-blue-600/10 dark:border-blue-400/10"
                          : item.disabled
                            ? "opacity-50 cursor-not-allowed"
                            : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/80"
                      )}
                    >
                      <div className={cn(
                        "w-8 h-8 md:w-4 md:h-4 flex items-center justify-center rounded-lg md:bg-transparent transition-colors",
                        isActive ? "bg-blue-600/10 text-blue-600" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 group-hover:text-zinc-600"
                      )}>
                        <Icon className="w-4 h-4 shrink-0 transition-transform group-active:scale-90" />
                      </div>
                      <span className="flex-1 text-left font-medium md:font-normal">{item.label}</span>
                      {item.disabled && (
                        <span className="text-[0.6rem] font-bold uppercase tracking-tighter px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-500">
                          Soon
                        </span>
                      )}
                      <div className={cn(
                        "transition-all duration-300",
                        isActive ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"
                      )}>
                        <div className="w-1 h-1 rounded-full bg-blue-600" />
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950/40">
          <Button
            className="w-full h-12 md:h-10 font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 active:scale-95 transition-transform"
            onClick={saveSettings}
            disabled={!isLevel1}
          >
            Save All Changes
          </Button>
        </div>
      </aside>

      {/* Main Content Layout */}
      <main className={cn(
        "flex-1 overflow-hidden flex flex-col relative bg-white dark:bg-zinc-900",
        !mobileShowContent ? "hidden md:flex" : "flex"
      )}>
        {/* Mobile Header */}
        <div className="md:hidden flex items-center gap-3 px-4 h-16 border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md sticky top-0 z-20">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileShowContent(false)}
            className="w-10 h-10 p-0 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <ChevronsLeft className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
          </Button>
          <div className="flex flex-col">
            <span className="text-[10px] font-black tracking-widest text-blue-600 uppercase leading-none mb-1">Settings</span>
            <span className="text-sm font-bold text-zinc-800 dark:text-zinc-100">
              {SECTIONS.flatMap(s => s.items).find(i => i.id === activeTab)?.label}
            </span>
          </div>
        </div>

        <div className="absolute inset-0 bg-grid-zinc-200/[0.05] -z-10" />
        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-6 py-8 md:px-16 lg:px-24">
            {renderContent()}
          </div>
        </ScrollArea>

        {/* Mobile Save Button Overlay if needed, but the sidebar button is good enough for now */}
      </main>

      {/* Reset Schedule Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-zinc-200 dark:border-zinc-800 animate-in zoom-in-95 duration-200">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-950/20 flex items-center justify-center flex-shrink-0">
                <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Reset Schedule</h3>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  Select which categories of lecture events you want to purge from the scheduling system.
                </p>
              </div>
            </div>

            <div className="space-y-4 my-6">
              {/* Rolling and Draft Events (Required) */}
              <label className="flex items-start gap-3 p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50 opacity-90 cursor-not-allowed">
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  className="mt-1 h-4 w-4 rounded border-zinc-300 text-red-600 focus:ring-red-500"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-zinc-900 dark:text-zinc-50">Rolling & Draft Events</span>
                  <p className="text-[10px] text-zinc-500">
                    Future draft/solid events beyond lock window. Always purged during reset.
                  </p>
                </div>
              </label>

              {/* Past Events */}
              <label className="flex items-start gap-3 p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 cursor-pointer transition-all">
                <input
                  type="checkbox"
                  checked={deletePastEvents}
                  onChange={(e) => setDeletePastEvents(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-zinc-300 text-red-600 focus:ring-red-500"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-zinc-900 dark:text-zinc-50 flex items-center gap-1.5">
                    Past Events
                    <span className="text-[8px] font-black uppercase tracking-wider px-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 rounded">Optional</span>
                  </span>
                  <p className="text-[10px] text-zinc-500">
                    Purges all lecture events scheduled before the current date and time.
                  </p>
                </div>
              </label>

              {/* Locked Events */}
              <label className="flex items-start gap-3 p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 cursor-pointer transition-all">
                <input
                  type="checkbox"
                  checked={deleteLockedEvents}
                  onChange={(e) => setDeleteLockedEvents(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-zinc-300 text-red-600 focus:ring-red-500"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-zinc-900 dark:text-zinc-50 flex items-center gap-1.5">
                    Locked Events
                    <span className="text-[8px] font-black uppercase tracking-wider px-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 rounded">Optional</span>
                  </span>
                  <p className="text-[10px] text-zinc-500">
                    Purges all lecture events within the Lock-in window ({settings?.schedule_lock_hours ?? 24} hours from current time).
                  </p>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6 border-t border-zinc-100 dark:border-zinc-800 pt-4">
              <Button variant="ghost" size="sm" onClick={() => setShowResetModal(false)} className="h-9 px-4 rounded-xl text-xs font-semibold">
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-9 px-4 rounded-xl text-xs font-semibold bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/10"
                onClick={handleConfirmReset}
              >
                Confirm Reset
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Admin Management Section Component (L1-only)
// ═══════════════════════════════════════════════════════════
function AdminManagementSection() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', access_level: 9 });

  const fetchAdmins = async () => {
    try {
      const res = await api.get('/api/admins');
      setAdmins(res.data || []);
    } catch (err) {
      toast.error('Failed to load administrators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const resetForm = () => {
    setForm({ name: '', email: '', access_level: 9 });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (admin) => {
    setForm({
      name: admin.name || admin.username || '',
      email: admin.email,
      access_level: admin.access_level
    });
    setEditingId(admin.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const profile = getProfile();
    if (profile.id === id) {
      toast.error("You cannot delete your own account");
      return;
    }

    if (!window.confirm('Are you sure you want to remove this administrator?')) return;

    try {
      await api.delete(`/api/admins/${id}`);
      toast.success('Administrator removed');
      fetchAdmins();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to delete';
      toast.error(msg);
    }
  };

  const handleSave = async () => {
    if (!form.email || !form.name) {
      toast.error('Email and Name are required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/admins/${editingId}`, {
          name: form.name,
          access_level: form.access_level
        });
        toast.success('Administrator updated');
      } else {
        await api.post('/api/admins', {
          username: form.email,
          name: form.name,
          access_level: form.access_level,
          password: ""
        });
        toast.success('Account provisioned successfully');
      }
      resetForm();
      fetchAdmins();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminManagementSkeleton />;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-zinc-800 dark:text-zinc-100">Manage Users</h2>
          <p className="text-sm text-zinc-500">Control access levels for team members</p>
        </div>
        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white gap-2 w-full sm:w-auto h-10 md:h-9" onClick={() => { resetForm(); setShowForm(true); }}>
          <Plus className="w-4 h-4" /> Add Member
        </Button>
      </div>

      {showForm && (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border-2 border-dashed border-blue-200 dark:border-blue-900/50 p-5 md:p-6 space-y-4 shadow-sm animate-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-zinc-800 dark:text-zinc-100">{editingId ? 'Edit Team Member' : 'Add New Member'}</h4>
            <button onClick={resetForm} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Email Address</Label>
              <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} disabled={!!editingId} placeholder="user@example.com" className="h-11 md:h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Full Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="John Doe" className="h-11 md:h-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Role / Access Level</Label>
            <select
              className="flex h-11 md:h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-800 dark:bg-zinc-950"
              value={form.access_level}
              onChange={e => setForm(f => ({ ...f, access_level: parseInt(e.target.value) }))}
            >
              {ACCESS_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 sm:pt-2">
            <Button variant="ghost" onClick={resetForm} className="h-11 md:h-9">Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white h-11 md:h-9 shadow-lg shadow-blue-600/20" disabled={saving} onClick={handleSave}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {editingId ? 'Update' : 'Provision Account'}
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        {admins.map(a => (
          <div key={a.id} className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 transition-all hover:bg-zinc-50/50 dark:hover:bg-zinc-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 md:w-10 md:h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-100/50 dark:border-blue-900/50 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 md:w-4 md:h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-zinc-900 dark:text-zinc-50 truncate">{a.name || a.username}</span>
                  <span className={cn("text-[0.6rem] px-2 py-0.5 rounded-full font-black uppercase tracking-tighter shadow-sm", LEVEL_COLORS[a.access_level] || 'bg-zinc-100')}>
                    {ACCESS_LEVELS.find(l => l.value === a.access_level)?.label.split('—')[1] || 'Unknown'}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 truncate mt-0.5">{a.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 border-t sm:border-t-0 pt-3 sm:pt-0 justify-end">
              <button
                onClick={() => handleEdit(a)}
                className="flex items-center gap-2 px-4 py-2 sm:p-2 rounded-xl text-xs font-semibold sm:font-normal bg-zinc-100 dark:bg-zinc-800 sm:bg-transparent hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all text-zinc-600 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400"
              >
                <Pencil className="w-4 h-4" />
                <span className="sm:hidden">Edit</span>
              </button>
              <button
                onClick={() => handleDelete(a.id)}
                className="flex items-center gap-2 px-4 py-2 sm:p-2 rounded-xl text-xs font-semibold sm:font-normal bg-red-50 dark:bg-red-900/20 sm:bg-transparent hover:bg-red-100 dark:hover:bg-red-900/40 transition-all text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <Trash2 className="w-4 h-4" />
                <span className="sm:hidden">Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

